
import { ParsedUrlParams, UrlParams } from "./urlparams";
import ClickData from "./clickdata";
import { GoogleAnalytics } from "./googleanalytics";
import Cacheable from "./cacheable";
import { MapCookie, PardotTypeEnum } from "./mapcookie";
import Referrer from "./referrer";
import { VERSION } from "./version";
import {Channel, ChannelGrouping} from "./channelgrouping";
import { safeNavigatorString, safeUrlHostname } from "./safeglobals";

export enum SessionProfileAttribute {

    // Google Analytics Data
    clientId = 'clientId',
    sessionId = 'sessionId',
    hitId = 'hitId',

    // Source Data
    referrer = 'referrer',
    channelGrouping = 'channelGrouping',
    channelType = 'channelType',

    // UTMs
    utmCampaignId = 'utmCampaignId',
    utmCampaignSource = 'utmCampaignSource',
    utmCampaignMedium = 'utmCampaignMedium',
    utmCampaignName = 'utmCampaignName',
    utmCampaignTerm = 'utmCampaignTerm',
    utmCampaignContent = 'utmCampaignContent',

    // Ads Metadata
    clickId = 'clickId',
    clickPlatform = 'clickPlatform',
    gclid = 'gclid',
    gbraid = 'gbraid',
    wbraid = 'wbraid',
    dclid = 'dclid',
    msclkid = 'msclkid',
    fbclid = 'fbclid',
    kcid = 'kcid',

    // HTTP Client metadata
    userAgent = 'userAgent',
    browserVendor = 'browserVendor',
    browserLanguage = 'browserLanguage',

    // MAP cookies
    activeDemandSessionGuidCookie = 'activeDemandSessionGuidCookie',
    actonWebsiteProspectorCookieKeys = 'actonWebsiteProspectorCookieKeys',
    actonWebsiteProspectorCookieValues = 'actonWebsiteProspectorCookieValues',
    hubspotContactCookie = 'hubspotContactCookie',
    pardotVisitorIdCookieKeys = 'pardotVisitorIdCookieKeys',
    pardotVisitorIdCookieValues = 'pardotVisitorIdCookieValues',
    pardotVisitorHashCookieKeys = 'pardotVisitorHashCookieKeys',
    pardotVisitorHashCookieValues = 'pardotVisitorHashCookieValues',

    // Lead Source data
    leadSources = 'leadSources',
    leadSourceCategories = 'leadSourceCategories',

    // Diagnostic Data
    diagnosticCookiesAllowed = 'diagnosticCookiesAllowed',
    diagnosticSessionStorageAllowed = 'diagnosticSessionStorageAllowed',
    diagnosticVersion = 'diagnosticVersion',
    sessionRestored = 'sessionRestored',

    // All Values
    allValues = 'allValues',

    // Lead Classification Attributes
    leadIsUnpaidOrBrandedPaid = 'leadIsUnpaidOrBrandedPaid',
    leadIsUnpaid = 'leadIsUnpaid',

    // Assisted conversion
    assistedConversionSource = 'assistedConversionSource',

    // Stored attributes not meant to be queried directly.
    urlParams = 'urlParams',

}

export interface SessionProfileQuery {
    attribute: SessionProfileAttribute;
    index: number;
}


export interface SessionProfile {

    // Google Analytics Data
    [SessionProfileAttribute.clientId]: Array<string>;
    [SessionProfileAttribute.sessionId]: Array<string>;
    [SessionProfileAttribute.hitId]: Array<string>;

    // Source Data
    [SessionProfileAttribute.referrer]: Array<string>;
    [SessionProfileAttribute.channelGrouping]: Array<string>;
    [SessionProfileAttribute.channelType]: Array<string>;

    // UTMs
    [SessionProfileAttribute.utmCampaignId]: Array<string>;
    [SessionProfileAttribute.utmCampaignSource]: Array<string>;
    [SessionProfileAttribute.utmCampaignMedium]: Array<string>;
    [SessionProfileAttribute.utmCampaignName]: Array<string>;
    [SessionProfileAttribute.utmCampaignTerm]: Array<string>;
    [SessionProfileAttribute.utmCampaignContent]: Array<string>;

    // Ads Metadata
    [SessionProfileAttribute.clickId] : Array<string>;
    [SessionProfileAttribute.clickPlatform]: Array<string>;
    [SessionProfileAttribute.gclid] : Array<string>;
    [SessionProfileAttribute.gbraid] : Array<string>;
    [SessionProfileAttribute.wbraid] : Array<string>;
    [SessionProfileAttribute.dclid] : Array<string>;
    [SessionProfileAttribute.msclkid] : Array<string>;
    [SessionProfileAttribute.fbclid] : Array<string>;
    [SessionProfileAttribute.kcid] : Array<string>;

    // HTTP Client metadata
    [SessionProfileAttribute.userAgent]: Array<string>;
    [SessionProfileAttribute.browserVendor]: Array<string>;
    [SessionProfileAttribute.browserLanguage]: Array<string>;

    // MAP cookies
    [SessionProfileAttribute.activeDemandSessionGuidCookie]: Array<string>;
    [SessionProfileAttribute.actonWebsiteProspectorCookieKeys]: Array<string>;
    [SessionProfileAttribute.actonWebsiteProspectorCookieValues]: Array<string>;
    [SessionProfileAttribute.hubspotContactCookie]: Array<string>;
    [SessionProfileAttribute.pardotVisitorIdCookieKeys] : Array<string>;
    [SessionProfileAttribute.pardotVisitorIdCookieValues] : Array<string>;
    [SessionProfileAttribute.pardotVisitorHashCookieKeys] : Array<string>;
    [SessionProfileAttribute.pardotVisitorHashCookieValues] : Array<string>;

    // Lead source data
    [SessionProfileAttribute.leadSources]: Array<string>;
    [SessionProfileAttribute.leadSourceCategories]: Array<string>;

    // Diagnostic Data
    [SessionProfileAttribute.diagnosticCookiesAllowed]: Array<boolean>;
    [SessionProfileAttribute.diagnosticSessionStorageAllowed]: Array<boolean>;
    [SessionProfileAttribute.diagnosticVersion]: Array<string>;
    [SessionProfileAttribute.sessionRestored]: Array<boolean>;

    // All Values is not an attribute of session profile
    // but a pointer to the entire profile's contents
    // (special field)

    // Lead Classification Attributes
    [SessionProfileAttribute.leadIsUnpaid]: Array<number>;
    [SessionProfileAttribute.leadIsUnpaidOrBrandedPaid]: Array<number>;

    // Assisted conversion
    [SessionProfileAttribute.assistedConversionSource]: Array<string>;

    // Array of JSON objects with ParsedUrlParams interface.
    [SessionProfileAttribute.urlParams]: Array<string>;
}

export interface SessionProfileQueryBinding {
    element: HTMLInputElement;
    query: SessionProfileQuery;
}

/**
 * Safely resolve a Web Storage area (localStorage / sessionStorage).
 *
 * Reading the storage *getter* can itself throw - Safari and Firefox raise
 * "SecurityError: The operation is insecure" when storage is blocked, which is
 * common for a third-party script (blocked cookies, ITP, private mode). A
 * `typeof window.localStorage` probe is therefore unsafe: it invokes the
 * throwing getter. We attempt the access inside try/catch and return null when
 * storage is unavailable, so callers can degrade instead of crashing.
 */
function safeStorage(kind: 'local' | 'session') : Storage | null {
    try {
        const store = kind === 'local' ? window.localStorage : window.sessionStorage;
        return store ? store : null;
    } catch (error) {
        return null;
    }
}

export class Loader
{

    protected cache: Cacheable;
    protected sessionExpiry: number;

    constructor(sessionExpiry: number = 1800000) {
        this.cache = new Cacheable();
        this.sessionExpiry = sessionExpiry;
    }

    public execute() : SessionProfile
    {
        // if session storage is already occupied, then skip the restore
        let restored = false;
        if (!this.hasSessionSet()) {
            restored = this.attemptSessionRestore();
        } else {
            restored = this.previouslyRestored();
        }

        // allow caching the urlparams in the profile
        // so that url param context is not lost when a profile is restored.
        const paramsJson = this.findOrCalculate(SessionProfileAttribute.urlParams, [JSON.stringify(UrlParams.parse())]);
        let params: ParsedUrlParams = JSON.parse(paramsJson[0]);
        // if params are empty, then check again and reset
        if (Object.keys(params).length === 0) {
            params = UrlParams.parse();
            if (Object.keys(params).length > 0) {
                this.cache.store.set(`fountain-analytics-${SessionProfileAttribute.urlParams}`, [JSON.stringify(params)]);
            }
        }

        // always parse click data fresh from the params, which may be cached.
        const clickData = ClickData.parse(params);

        const gclid = this.findOrCalculate(SessionProfileAttribute.gclid, UrlParams.get('gclid', params));
        const gbraid = this.findOrCalculate(SessionProfileAttribute.gbraid, UrlParams.get('gbraid', params));
        const wbraid = this.findOrCalculate(SessionProfileAttribute.wbraid, UrlParams.get('wbraid', params));
        const dclid = this.findOrCalculate(SessionProfileAttribute.dclid, UrlParams.get('dclid', params));
        const msclkid = this.findOrCalculate(SessionProfileAttribute.msclkid, UrlParams.get('msclkid', params));
        const fbclid = this.findOrCalculate(SessionProfileAttribute.fbclid, UrlParams.get('fbclid', params));
        const kcid = this.findOrCalculate(SessionProfileAttribute.kcid, UrlParams.get('kcid', params));

        // calculated ahead of source/medium polyfill below, since the referral
        // polyfill needs to know the channel grouping and referrer.
        const referrer = this.findOrCalculate(SessionProfileAttribute.referrer, Referrer.get());
        const channelGroupingResult = ChannelGrouping.get(params);
        const channelGrouping = this.findOrCalculate(SessionProfileAttribute.channelGrouping, [channelGroupingResult.channel]);
        const channelType = this.findOrCalculate(SessionProfileAttribute.channelType, [channelGroupingResult.type]);

        let originalSource = this.findOrCalculate(SessionProfileAttribute.utmCampaignSource, UrlParams.get('utm_source', params));
        let originalMedium = this.findOrCalculate(SessionProfileAttribute.utmCampaignMedium, UrlParams.get('utm_medium', params));
        let source: Array<string> = [];
        let medium: Array<string> = [];
        // calculate commonly associated source / medium
        // based on click identifier / platform
        if (gclid.length || gbraid.length || wbraid.length) {
            source = ['google'];
            medium = ['cpc'];
        } else if (dclid.length) {
            source = ['google'];
            medium = ['display'];
        } else if (msclkid.length) {
            source = ['bing'];
            medium = ['cpc'];
        } else if (fbclid.length) {
            source = ['meta'];
            medium = ['paid-social'];
        } else if (channelGrouping.indexOf(Channel.Referral) !== -1 && !originalSource.length && !originalMedium.length && referrer.length) {
            // referral channel with no explicit utm hints: attribute the visit
            // to the referring site. Prefer the parsed hostname, but fall back
            // to the raw referrer value if it can't be parsed (e.g. no URL
            // constructor on IE11) rather than leaving source empty - a
            // referral-classified lead with no source at all is the exact
            // reporting gap this polyfill exists to close.
            const hostname = safeUrlHostname(referrer[0]);
            source = [hostname.length ? hostname : referrer[0]];
            medium = ['referral'];
        }
        // respect the original source/medium if provided
        if (originalSource.length) {
            source = originalSource;
        }
        if (originalMedium.length) {
            medium = originalMedium;
        }

        const assistedConversionSource = this.findOrCalculate(
            SessionProfileAttribute.assistedConversionSource,
            UrlParams.get('assist_src', params)
        );

        // calculate unpaid lead
        let unpaidLead = this.calculateUnpaidLead(channelGrouping);

        // calculate unpaid and branded
        const campaignName = this.findOrCalculate(SessionProfileAttribute.utmCampaignName, UrlParams.getOneOf(['utm_campaign', 'campaign'], params));
        let unpaidOrBrandedLead = this.calculateUnpaidOrBrandedPaidLead(campaignName, unpaidLead);

        const profile = {
            // Google Analytics Data
            [SessionProfileAttribute.clientId]: this.findOrCalculate(SessionProfileAttribute.clientId, GoogleAnalytics.getClientIds()),
            [SessionProfileAttribute.sessionId]: this.findOrCalculate(SessionProfileAttribute.sessionId, GoogleAnalytics.getSessionIds()),
            [SessionProfileAttribute.hitId]: GoogleAnalytics.getHitIds(),  // not cached!
        
            // Source Data - Always one of each so wrap in array for API consistency
            [SessionProfileAttribute.referrer]: referrer,
            [SessionProfileAttribute.channelGrouping]: channelGrouping,
            [SessionProfileAttribute.channelType]: channelType,

            // UTMs
            [SessionProfileAttribute.utmCampaignId]: this.findOrCalculate(SessionProfileAttribute.utmCampaignId, UrlParams.get('utm_id', params)),
            [SessionProfileAttribute.utmCampaignSource]: source,
            [SessionProfileAttribute.utmCampaignMedium]: medium,
            [SessionProfileAttribute.utmCampaignName]: campaignName,
            [SessionProfileAttribute.utmCampaignTerm]: this.findOrCalculate(SessionProfileAttribute.utmCampaignTerm, UrlParams.getOneOf(['utm_term','keyword'], params)),
            [SessionProfileAttribute.utmCampaignContent]: this.findOrCalculate(SessionProfileAttribute.utmCampaignContent, UrlParams.get('utm_content', params)),
        
            // Ads Metadata
            [SessionProfileAttribute.clickId] : this.findOrCalculate(SessionProfileAttribute.clickId, clickData.id),
            [SessionProfileAttribute.clickPlatform]: this.findOrCalculate(SessionProfileAttribute.clickPlatform, clickData.platform),
            [SessionProfileAttribute.gclid] : gclid,
            [SessionProfileAttribute.gbraid] : gbraid,
            [SessionProfileAttribute.wbraid] : wbraid,
            [SessionProfileAttribute.dclid] : dclid,
            [SessionProfileAttribute.msclkid] : msclkid,
            [SessionProfileAttribute.fbclid] : fbclid,
            [SessionProfileAttribute.kcid] : kcid,

            // HTTP Client metadata - naturally persistent values so not cached.
            // Read via safeNavigatorString: bots stub navigator's property
            // getters with buggy code, so a bare `window.navigator.userAgent`
            // read can throw (RangeError/ReferenceError from Navigator.get) and
            // take the whole profile down with it. Guarded reads degrade to ''
            // instead - see ./safeglobals and REFERENCE.md §11 invariant #2.
            [SessionProfileAttribute.userAgent]: [safeNavigatorString('userAgent')],
            [SessionProfileAttribute.browserVendor]: [safeNavigatorString('vendor')],
            [SessionProfileAttribute.browserLanguage]: [safeNavigatorString('language')],
        
            // MAP cookies - naturally persistent values so not cached
            [SessionProfileAttribute.activeDemandSessionGuidCookie]: MapCookie.getActiveDemand(this.cache.store.cookie),
            [SessionProfileAttribute.actonWebsiteProspectorCookieKeys]: MapCookie.getActOn(this.cache.store.cookie, false),
            [SessionProfileAttribute.actonWebsiteProspectorCookieValues]: MapCookie.getActOn(this.cache.store.cookie, true),
            [SessionProfileAttribute.hubspotContactCookie]: MapCookie.getHubSpot(this.cache.store.cookie),
            [SessionProfileAttribute.pardotVisitorIdCookieKeys] : MapCookie.getPardotVisitor(this.cache.store.cookie, PardotTypeEnum.VisitorIdKey),
            [SessionProfileAttribute.pardotVisitorIdCookieValues] : MapCookie.getPardotVisitor(this.cache.store.cookie, PardotTypeEnum.VisitorIdValue),
            [SessionProfileAttribute.pardotVisitorHashCookieKeys] : MapCookie.getPardotVisitor(this.cache.store.cookie, PardotTypeEnum.VisitorHashKey),
            [SessionProfileAttribute.pardotVisitorHashCookieValues] : MapCookie.getPardotVisitor(this.cache.store.cookie, PardotTypeEnum.VisitorHashValue),
        
            // Assisted conversion - cached so first-touch value is preserved within session
            [SessionProfileAttribute.assistedConversionSource]: assistedConversionSource,

            // Lead source data - always calculated fresh so not cached
            [SessionProfileAttribute.leadSources]: [] as Array<string>,
            [SessionProfileAttribute.leadSourceCategories]: [] as Array<string>,

            // Diagnostic Data - naturally persistent values so not cached
            [SessionProfileAttribute.diagnosticCookiesAllowed]: [this.cache.store.cookie.status],
            [SessionProfileAttribute.diagnosticSessionStorageAllowed]: [this.cache.store.session.status],
            [SessionProfileAttribute.diagnosticVersion]: [VERSION],
            // report whether the session was restored from localStorage
            [SessionProfileAttribute.sessionRestored]: [restored],

            // Lead Classification - always calculated fresh
            [SessionProfileAttribute.leadIsUnpaid]: unpaidLead,
            [SessionProfileAttribute.leadIsUnpaidOrBrandedPaid]: unpaidOrBrandedLead,

            [SessionProfileAttribute.urlParams]: paramsJson,

        };
        if (!restored) {
            this.setRestorePoint(profile);
        } else {
            // only set the session restore flag
            // if we restored sessionStorage from localStorage
            const sessionStore = safeStorage('session');
            if (sessionStore) {
                try {
                    sessionStore.setItem('fountain-analytics-restored', '1');
                } catch (error) {
                    console.debug(error);
                }
            }
        }
        // the restore point timer for the localStorage profile
        // only expires if the period passes without any web activity
        this.resetRestorePointTimer();
        return profile;
    }

    calculateUnpaidLead(channelGrouping: Array<string>) : Array<number>
    {
        let unpaidLead = [];
        if (channelGrouping.length > 0) {
            for (let i = 0; i < channelGrouping.length; i ++) {
                // evaluate the current channel against the paid channels
                const isPaid = ['Paid Search', 'Display', 'Paid Social'].indexOf(channelGrouping[i]) === -1;
                unpaidLead.push(Number(isPaid));
            }
        }
        return unpaidLead;
    }

    calculateUnpaidOrBrandedPaidLead(campaignName : Array<string>, unpaidLead: Array<number>) : Array<number>
    {
        let unpaidOrBrandedLead = [];
        if (unpaidLead.length > 0) {
            for (let i = 0; i < unpaidLead.length; i ++) {
                let isUnpaidLeadOrBrandedLead = unpaidLead[i];
                if (!isUnpaidLeadOrBrandedLead) {
                    if (campaignName.length > i) {
                        const campaignNameMember = campaignName[i];
                        const isBrandedCampaign = campaignNameMember.toLowerCase().indexOf('brand') !== -1;
                        if (isBrandedCampaign) {
                            isUnpaidLeadOrBrandedLead = 1;
                        }
                    }
                }
                unpaidOrBrandedLead.push(isUnpaidLeadOrBrandedLead);
            }
        }
        return unpaidOrBrandedLead;
    }

    findOrCalculate(key: string, value: Array<string>) {
        return this.cache.get(`fountain-analytics-${key}`, () => {return value});
    }

    hasSessionSet() : boolean
    {
        const sessionIds = this.cache.get(
            'fountain-analytics-' + SessionProfileAttribute.sessionId, 
            // callback should return empty here
            // to illustrate that there is no session currently set
            () => { return []; }
        );
        return sessionIds.length > 0;
    }

    previouslyRestored() : boolean
    {
        const sessionStore = safeStorage('session');
        if (sessionStore) {
            try {
                return sessionStore.getItem('fountain-analytics-restored') === '1';
            } catch (error) {
                return false;
            }
        }
        return false;
    }

    /**
     * Attempts to restore a session profile from the last restore point
     * in localStorage (so long as it is valid and not expired)
     * 
     * @returns boolean whether restore was successful or not
     */
    attemptSessionRestore() : boolean
    {
        // Resolve storage up front. Accessing the localStorage / sessionStorage
        // getter can itself throw ("operation is insecure" when storage is blocked
        // in a third-party context); safeStorage turns that into a null result.
        const sessionStore = safeStorage('session');
        const localStore = safeStorage('local');
        if (this.previouslyRestored() || !localStore || !sessionStore) {
            return false;
        }

        // check for a profile in local storage
        let maybeProfile: string | null = null;
        let profileTimeString: string | null = null;
        try {
            maybeProfile = localStore.getItem('fountain-analytics-rp');
            profileTimeString = localStore.getItem('fountain-analytics-rp-time');
        } catch (error) {
            console.debug(error);
            return false;
        }
        if (profileTimeString === null) {
            return false;
        }
        const profileTime = parseInt(profileTimeString);
        if (Number.isNaN(profileTime)) {
            return false;
        }
        const currentTs = Date.now();
        if (profileTime < currentTs - this.sessionExpiry) {
            // expired - localstorage set more than max time ago
            this.removeRestorePoint();
            return false;
        }
        let profile = null;
        try {
            // profile is actually a SessionProfile
            // serialized to JSON
            // but there is no guarantee of type safety
            // so we have to capture, catch and validate
            profile = JSON.parse(maybeProfile);
        } catch (e) {
            console.error(e);
        }
        // note: typeof null === 'object', so the null check is required here -
        // profile is null when JSON.parse threw above or the stored value was "null"
        if (profile !== null && typeof(profile) === 'object') {
            const keys = Object.keys(profile);
            // distribute the recovered profile's data to the cache atomically:
            // if any write fails partway, roll back everything written this pass.
            // otherwise a partial profile (e.g. sessionId written but the rest not)
            // would be treated as a complete session on the next load.
            const written: Array<string> = [];
            for (let i = 0; i < keys.length; i ++) {
                const storeKey = `fountain-analytics-${keys[i]}`;
                const result = this.cache.store.set(storeKey, profile[keys[i]]);
                if (!result) {
                    // storage failure mid-restore: undo the partial write
                    for (let j = 0; j < written.length; j ++) {
                        this.cache.store.remove(written[j]);
                    }
                    return false;
                }
                written.push(storeKey);
            }
            return true;
        }
        return false;
    }

    removeRestorePoint() : void
    {
        const localStore = safeStorage('local');
        if (localStore) {
            try {
                localStore.removeItem('fountain-analytics-rp');
                localStore.removeItem('fountain-analytics-rp-time');
            } catch (error) {
                console.debug(error);
            }
        }
    }

    /**
     * Sets the last restore point from the session
     * 
     * @param profile 
     */
    setRestorePoint(profile: SessionProfile) : void
    {
        const localStore = safeStorage('local');
        if (localStore) {
            try {
                localStore.setItem('fountain-analytics-rp', JSON.stringify(profile));
            } catch (error) {
                console.debug(error);
            }
        }
    }

    /**
     * Reset the timer for localStorage restore point
     */
    resetRestorePointTimer() : void
    {
        const localStore = safeStorage('local');
        if (localStore) {
            try {
                localStore.setItem('fountain-analytics-rp-time', Date.now().toString());
            } catch (error) {
                console.debug(error);
            }
        }
    }

}
